import { dia } from "@clientio/rappid";
import * as ace from "ace-builds";
import { StateView, State } from "../mvc/state";
import { AceFactory } from "../language-server/ace-factory";
import { PreferenceStore } from "../services/preference-store";
import AceLanguageServer from "../language-server/ace-lsp-adapter";
import { setSyntaxHighlighting } from "../language-server/ace-settings";
import { actionState } from "./action-state";

export class AceEditorState {
  private containerGroup: SVGGElement;
  private foreignObject: SVGForeignObjectElement;
  private editorDiv: HTMLDivElement;
  private editor: ace.Ace.Editor;
  private stateView: StateView;

  mount(cellView: dia.CellView): void {
    this.stateView = cellView as StateView;
    const state = this.stateView.getState();
    const bbox = state.getBBox();

    const x = StateView.Left;
    const y = StateView.Top + StateView.MarginTop;
    const width = bbox.width - StateView.Left * 2;

    const regions = state.getEmbeddedRegions();
    const height =
      regions.length > 0 && !state.getSubdiagram()
        ? state.getEditorHeight()
        : bbox.height - StateView.MarginTop - StateView.Top - StateView.Padding;

    this.containerGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
    this.containerGroup.setAttribute("class", "ace-editor");

    this.foreignObject = document.createElementNS("http://www.w3.org/2000/svg", "foreignObject");
    this.foreignObject.setAttribute("x", x.toString());
    this.foreignObject.setAttribute("y", y.toString());
    this.foreignObject.setAttribute("width", width.toString());
    this.foreignObject.setAttribute("height", height.toString());

    this.editorDiv = document.createElementNS("http://www.w3.org/1999/xhtml", "div") as HTMLDivElement;
    this.editorDiv.setAttribute("id", "ace-highlighter-" + state.id);
    this.editorDiv.className = "ace-highlighter-editor";
    this.editorDiv.style.width = width + "px";
    this.editorDiv.style.height = height + "px";
    this.editorDiv.style.pointerEvents = "auto";

    this.foreignObject.appendChild(this.editorDiv);
    this.containerGroup.appendChild(this.foreignObject);
    this.stateView.el.appendChild(this.containerGroup);

    this.editorDiv.addEventListener("dblclick", (evt) => {
      evt.stopPropagation();
      if (!this.editor?.isFocused()) {
        this.setFocus(this.stateView, true);
      }
    });

    const darkMode = PreferenceStore.INSTANCE.theme.isDark;
    this.editor = AceFactory.createEmbeddedEditor(this.editorDiv, {
      padding: 2,
      darkMode: darkMode,
    });

    this.editor.setReadOnly(true);

    this.editor.on("change", (delta: ace.Ace.Delta) => {
      if (!this.editor?.isFocused()) {
        return;
      }
      this.stateView.trigger("change:specification:text", this.editor, delta);
    });

    Array.from(this.editorDiv.children).forEach((child) => {
      (child as HTMLElement).style.pointerEvents = "none";
    });

    this.updatePointerEvents();
    this.stateView.selection?.collection.on("add remove reset", this.updatePointerEvents, this);

    this.editor.on("focus", () => {
      if (state.getShowSpecification()) {
        AceLanguageServer.getInstance().connect(this.editor, "state");
      }
      if (PreferenceStore.INSTANCE.isDark) {
        this.editorDiv.style.borderColor = "var(--state-dark-stroke-color)";
      } else {
        this.editorDiv.style.borderColor = "var(--state-light-stroke-color)";
      }
      this.stateView.trigger("batchcommand:init");
      this.updatePointerEvents();
    });

    this.editor.on("blur", () => {
      AceLanguageServer.getInstance().disconnect(this.editor);
      this.editorDiv.style.borderColor = "";
      this.stateView.trigger("batchcommand:store");
      this.updatePointerEvents();
    });

    let lastShowSpec = state.getShowSpecification();
    const text = lastShowSpec ? state.attr(["specification", "text"]) : state.attr(["documentation", "text"]);
    this.editor.getSession().setValue(text || "");
    setSyntaxHighlighting(this.editor, !lastShowSpec);

    if (this.editor) {
      this.editor.resize(true);
    }

    state.on("change:attrs", () => {
      const newShowSpec = state.getShowSpecification();
      const newText = newShowSpec ? state.attr(["specification", "text"]) : state.attr(["documentation", "text"]);
      if (this.editor && newShowSpec !== lastShowSpec) {
        lastShowSpec = newShowSpec;
        setSyntaxHighlighting(this.editor, !newShowSpec);
        this.editor.getSession().setValue(newText || "");
      } else if (this.editor && newText !== this.editor.getValue()) {
        this.editor.getSession().setValue(newText || "");
      }
    });
  }

  unmount(): void {
    this.stateView.selection?.collection.off("add remove reset", this.updatePointerEvents, this);
    if (this.editor) {
      AceLanguageServer.getInstance().disconnect(this.editor);
      this.editor.destroy();
      this.editor = null;
    }
    if (this.containerGroup) {
      this.containerGroup.remove();
      this.containerGroup = null;
    }
    this.foreignObject = null;
    this.editorDiv = null;
    this.stateView = null;
  }

  isMounted(): boolean {
    if (!this.stateView) {
      return false;
    }
    const container = this.stateView.el.querySelector(".ace-editor");
    return container !== null;
  }

  getEditor(): ace.Ace.Editor | undefined {
    return this.editor;
  }

  isFocused(): boolean {
    return this.editor?.isFocused() || false;
  }

  setFocus(_view: StateView, focus: boolean): void {
    if (!this.editor) return;

    this.editor.setReadOnly(!focus || PreferenceStore.INSTANCE.isReadOnly);
    if (focus) {
      this.editor.focus();
      this.editor.getSelection().moveCursorFileEnd();
    } else {
      this.editor.blur();
      this.editor.getSelection().clearSelection();
    }
  }

  private updatePointerEvents(): void {
    if (!this.editorDiv || !this.stateView.selection) return;

    if (this.editor?.isFocused()) {
      // Editor is focused, allow interaction
      this.editorDiv.style.pointerEvents = "auto";
      Array.from(this.editorDiv.children).forEach((child) => {
        (child as HTMLElement).style.pointerEvents = "auto";
      });
    } else if (
      this.stateView.selection.collection.length > 1 &&
      this.stateView.selection.collection.models.some((m) => m.id === this.stateView.model.id)
    ) {
      this.editorDiv.style.pointerEvents = "none";
    } else {
      this.editorDiv.style.pointerEvents = "auto";
      Array.from(this.editorDiv.children).forEach((child) => {
        (child as HTMLElement).style.pointerEvents = "none";
      });
    }
  }

  editorUndoRedoHandler(model: State): void {
    if (!this.editor) return;

    const text = model.attr(["specification", "text"]);
    if (text !== this.editor.getValue()) {
      if (actionState.isUndo) {
        this.editor.undo();
      } else if (actionState.isRedo) {
        this.editor.redo();
      }
    }
  }

  resize(): void {
    if (!this.foreignObject || !this.editor || !this.stateView) return;

    const state = this.stateView.getState();
    const bbox = state.getBBox();

    const width = Math.max(bbox.width - StateView.Left * 2, 10);

    const regions = state.getEmbeddedRegions();
    const height =
      regions.length > 0 && !state.getSubdiagram()
        ? Math.max(state.getEditorHeight(), 10)
        : Math.max(bbox.height - StateView.MarginTop - StateView.Top - StateView.Padding, 10);

    this.foreignObject.setAttribute("width", width.toString());
    this.foreignObject.setAttribute("height", height.toString());
    this.editorDiv.style.width = width + "px";
    this.editorDiv.style.height = height + "px";

    this.editor.resize();
  }

  getEditorContainer(): HTMLDivElement | undefined {
    return this.editorDiv;
  }
}
